/**
 * Authoring a snooze instant — WD-1 (0030, D-050).
 *
 * **THIS FILE AUTHORS; IT NEVER CLASSIFIES.** `snooze_my_day_item` takes an
 * absolute instant, and an instant has to be composed from the wall time
 * Brandon picked SOMEWHERE. It is composed here **in the canonical Crewlink
 * timezone — never the device's** (INV-T17): *"until tomorrow 7am"* is a
 * promise about his working day, exactly as a due time is, and the due-time
 * rule applies unchanged. Whether a snooze has lapsed is decided by the
 * DATABASE (`list_my_day_items` computes the `snoozed` bucket against its own
 * clock); nothing in this file, and nothing in the client, ever compares
 * `snoozed_until` with a clock of its own.
 *
 * **WHY THE CLIENT AND NOT THE SERVER:** a due time is converted by Postgres
 * (`at time zone`) because the wall time itself is STORED — `due_time` is
 * product truth. A snooze stores only the instant, so there is no wall-time
 * column for the server to compose from; the composition happens once, here,
 * at the moment of authoring, using the same tzdata `Intl` renders with.
 */

/** `Date.UTC` of the wall-clock reading that `zone` shows for `at`. */
function wallClockAsUtc(zone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const read = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  // `hour12: false` can yield "24" at midnight in some runtimes; normalise.
  return Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second'),
  );
}

/**
 * The instant at which `zone`'s wall clock reads `wall` (a `datetime-local`
 * value, `yyyy-mm-ddThh:mm`), as an ISO string — or null when the input does
 * not parse.
 *
 * Two-pass fixed point over the zone's UTC offset, so a wall time on either
 * side of a DST transition resolves against the offset actually in force
 * there rather than the one in force now. A wall time that does not exist
 * (the spring-forward gap) has no fixed point; the second pass settles on an
 * instant inside the transition hour, deterministically, rather than failing
 * — a snooze asked for in a minute the clocks skipped still lands within
 * that hour. An ambiguous fall-back wall time resolves deterministically to
 * one of its two readings.
 */
export function wallTimeToInstant(zone: string, wall: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wall);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match;
  const asUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  if (Number.isNaN(asUtc)) return null;
  try {
    // Guess: the wall time read as UTC, corrected by the offset at that guess…
    const first = asUtc - (wallClockAsUtc(zone, new Date(asUtc)) - asUtc);
    // …then corrected again with the offset at the CORRECTED instant, which is
    // what settles a pick that lands across a DST boundary from now.
    const settled = asUtc - (wallClockAsUtc(zone, new Date(first)) - first);
    return new Date(settled).toISOString();
  } catch {
    // An unknown zone: refuse rather than silently authoring in UTC.
    return null;
  }
}
